import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useUniqueId } from '../../hooks/use-unique-id';
import { RadioInput, RadioInputProps } from '../radio-input';
import { Typography } from '../typography';

import classes from './radio.module.scss';

export interface RadioProps extends Omit<RadioInputProps, 'label'> {
  /**
   * Custom classname
   */
  className?: string;

  /**
   * An optional variable that defines a specific variant or style configuration
   * for an element, component, or feature. The `variant` property can be set
   * to customize the appearance or behavior.
   *
   * Possible value:
   * - `'compact'`: Indicates the compact style (no paddings) variant is applied.
   */
  variant?: 'compact';

  /**
   * Render as disabled
   */
  disabled?: boolean;

  /**
   * Custom label name
   */
  label?: React.ReactNode;

  /**
   * Render as checked
   */
  checked?: boolean;

  /**
   * Custom click handler
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const Radio: React.FC<RadioProps> = React.memo(({
  id,
  label,
  checked = false,
  disabled = false,
  variant,
  className,
  ...props
}) => {
  const inputId = useUniqueId('checkbox-input-', id);
  const wrapperClass = useMemo(() => clsx(
    classes.root,
    {
      [classes.disabled]: disabled,
      [classes.active]: checked,
      [classes.compact]: variant === 'compact'
    },
    className
  ), [className, disabled, variant, checked]);

  return (
    <label className={wrapperClass}>
      <span className={classes.inputWrapper}>
        <RadioInput
          id={inputId}
          disabled={disabled}
          checked={checked}
          className={classes.input}
          {...props}
        />
      </span>

      {label && (
        <Typography htmlFor={inputId} as="span" variant="paragraph" className={classes.label}>
          {label}
        </Typography>
      )}
    </label>
  );
});
